import { HttpClient } from '@angular/common/http';
import { Component, inject, type OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import * as _ from 'lodash';
import { Subscription } from 'rxjs';
import * as SuperAdminOrgGet from '../../../server/routes/super-admin/orgs/get';
import { generatePath } from '../../../server/services/utils.service';
import type {
  Org,
  OrgSpace,
  OrgSpaceUser,
  OrgUser,
} from '../../../server/types/org-types';
import { TitleService } from '../title.service';
import { UtilsService } from '../utils.service';

type OrgUserWithAccountLinkAndSpaceNames = OrgUser & {
  accountLink?: string;
  createdAtString: string;
  updatedAtString: string;
  archivedAtString: string;
  userSpaces: { name: string; href: string }[];
};

type OrgUserWithJoinedData = OrgSpaceUser &
  OrgUserWithAccountLinkAndSpaceNames & { createdAtString: string };

type OrgSpaceWithUserData = OrgSpace & {
  users: OrgUserWithJoinedData[];
  createdAtString: string;
  updatedAtString: string;
  archivedAtString: string;
  taskNumber: number;
};

type OrgDetail = Org & {
  createdAtString: string;
  updatedAtString: string;
  lockedAtString: string;
  deletedAtString: string;
  users: OrgUserWithAccountLinkAndSpaceNames[];
  spaces: OrgSpaceWithUserData[];
};

@Component({
  selector: 'app-super-admin-org-detail',
  templateUrl: './super-admin-org-detail.component.html',
  styleUrls: ['./super-admin-org-detail.component.scss'],
  standalone: false,
})
export class SuperAdminOrgDetailComponent implements OnInit {
  private http = inject(HttpClient);
  private titleService = inject(TitleService);
  private utils = inject(UtilsService);
  private route = inject(ActivatedRoute);

  orgId: string;
  org: OrgDetail | undefined;

  userDisplayedColumns: string[] = [
    'name',
    'email',
    'admin',
    'userSpaces',
    'createdAtString',
    'updatedAtString',
    'archivedAtString',
    '_id',
    'accountId',
  ];

  spaceDisplayedColumns: string[] = [
    'name',
    'users',
    'taskNumber',
    'createdAtString',
    'updatedAtString',
    'archivedAtString',
    '_id',
  ];

  waiting = true;
  accessDenied = false;
  errorState?: 'unknownError';

  subscriptions = new Subscription();

  ngOnInit() {
    this.titleService.set('Org detail - Super admin');

    this.orgId = this.route.snapshot.params.orgId;

    const path = generatePath<SuperAdminOrgGet.ReqParams>(
      '/api/super-admin/orgs/:orgId',
      { orgId: this.orgId }
    );

    const httpSubscribe = this.http
      .get<SuperAdminOrgGet.ResBody>(path)
      .subscribe({
        next: ({ org, taskNumbers }) => {
          const users = org.users.map((user) => {
            const userSpaces = org.spaces
              .filter(
                (space) =>
                  space.users.filter((u) => u._id === user._id).length > 0
              )
              .map(({ name, _id }) => ({ name, href: _id }));
            const updatedUser: OrgUserWithAccountLinkAndSpaceNames = {
              ...user,
              createdAtString: new Date(user.createdAt).toLocaleDateString(),
              updatedAtString: new Date(user.updatedAt).toLocaleDateString(),
              archivedAtString: user.archivedAt
                ? new Date(user.archivedAt).toLocaleDateString()
                : '',
              accountLink: '/super-admin/account/' + user.accountId,
              userSpaces,
            };
            return _.cloneDeep(updatedUser);
          });

          const spaces = org.spaces.map((space) => {
            const usersWithJoinedData = space.users.map((u) => {
              const found = users.find((s) => s._id === u._id);
              const valueToUse: OrgUserWithAccountLinkAndSpaceNames = found ?? {
                _id: u._id,
                name: 'User name not found in org users',
                color: 0,
                email: 'Email not found in org users',
                admin: false,
                createdAt: 0,
                updatedAt: 0,
                createdAtString: '',
                updatedAtString: '',
                archivedAtString: '',
                userSpaces: [],
              };
              const userWithJoinedData: OrgUserWithJoinedData = {
                ...u,
                ...valueToUse,
              };
              return userWithJoinedData;
            });

            const updatedSpace: OrgSpaceWithUserData = {
              ...space,
              users: usersWithJoinedData,
              createdAtString: new Date(space.createdAt).toLocaleDateString(),
              updatedAtString: new Date(space.updatedAt).toLocaleDateString(),
              archivedAtString: space.archivedAt
                ? new Date(space.archivedAt).toLocaleDateString()
                : '',
              taskNumber:
                taskNumbers.find((t) => t.spaceId === space._id)?.taskNumber ??
                -1,
            };
            return updatedSpace;
          });

          this.org = {
            ...org,
            createdAtString: new Date(org.createdAt).toLocaleDateString(),
            updatedAtString: new Date(org.updatedAt).toLocaleDateString(),
            lockedAtString: org.lockedAt
              ? new Date(org.lockedAt).toLocaleDateString()
              : '',
            deletedAtString: org.deletedAt
              ? new Date(org.deletedAt).toLocaleDateString()
              : '',
            users,
            spaces,
          };
        },
        error: (error) => {
          this.waiting = false;
          if (error.status === 0) {
            this.utils.launchOfflineNotification();
          } else if (error.status === 403) {
            this.accessDenied = true;
          } else {
            this.errorState = 'unknownError';
            console.error(error);
          }
        },
      });
    this.subscriptions.add(httpSubscribe);
  }
}
